/**
 * 🔊 GAME SOUNDS - Sons pour les jeux
 * Utilise l'API Web Audio pour des sons de qualité
 */

// URLs de sons libres de droits (freesound.org compatible)
const SOUND_URLS = {
  // Sons de base générés par Web Audio API
  spin: null,      // Son de rotation
  tick: null,      // Cliquetis
  coin: null,      // Pièce qui tombe
  win: null,       // Victoire
  jackpot: null,   // Jackpot
  dice: null,      // Dés qui roulent
  scratch: null,   // Grattage
  flip: null,      // Carte retournée
  click: null,     // Clic bouton
  whoosh: null,    // Mouvement rapide
};

// Context audio global
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Générateur de sons synthétiques
export const GameSounds = {
  // Initialiser le contexte audio (doit être appelé après une interaction utilisateur)
  init: () => {
    try {
      getAudioContext();
      return true;
    } catch (e) {
      console.warn('Audio not supported');
      return false;
    }
  },

  // Son de cliquetis pour la roue
  tick: (pitch = 1) => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 800 * pitch;
      osc.type = 'square';
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialDecayTo && gain.gain.exponentialDecayTo(0.01, ctx.currentTime + 0.05);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {}
  },

  // Son de rotation de rouleau
  slotSpin: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 150;
      osc.type = 'sawtooth';
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  },

  // Son d'arrêt de rouleau
  slotStop: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 400;
      osc.type = 'triangle';
      
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {}
  },

  // Son de pièces qui tombent
  coins: () => {
    try {
      const ctx = getAudioContext();
      
      for (let i = 0; i < 8; i++) {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = 2000 + Math.random() * 2000;
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.1);
        }, i * 50);
      }
    } catch (e) {}
  },

  // Son de victoire
  win: () => {
    try {
      const ctx = getAudioContext();
      const notes = [523.25, 659.25, 783.99, 1046.50]; // Do Mi Sol Do (octave)
      
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        }, i * 100);
      });
    } catch (e) {}
  },

  // Son de JACKPOT (fanfare)
  jackpot: () => {
    try {
      const ctx = getAudioContext();
      // Fanfare triomphante
      const melody = [
        { freq: 523, time: 0 },
        { freq: 659, time: 100 },
        { freq: 784, time: 200 },
        { freq: 1047, time: 300 },
        { freq: 1047, time: 500 },
        { freq: 988, time: 600 },
        { freq: 1047, time: 700 },
      ];
      
      melody.forEach(({ freq, time }) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'square';
          
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.15);
        }, time);
      });
      
      // Ajouter les pièces
      setTimeout(() => GameSounds.coins(), 400);
    } catch (e) {}
  },

  // Son de dés qui roulent
  diceRoll: () => {
    try {
      const ctx = getAudioContext();
      
      for (let i = 0; i < 15; i++) {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(ctx.destination);
          
          filter.type = 'bandpass';
          filter.frequency.value = 1000 + Math.random() * 2000;
          
          osc.type = 'sawtooth';
          osc.frequency.value = 100 + Math.random() * 200;
          
          gain.gain.setValueAtTime(0.08, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.05);
        }, i * 80 + Math.random() * 40);
      }
    } catch (e) {}
  },

  // Son de grattage
  scratch: () => {
    try {
      const ctx = getAudioContext();
      const bufferSize = 4096;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const noise = ctx.createBufferSource();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      noise.buffer = noiseBuffer;
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      
      filter.type = 'bandpass';
      filter.frequency.value = 3000;
      filter.Q.value = 1;
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      
      noise.start(ctx.currentTime);
      noise.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  },

  // Son de carte retournée
  cardFlip: () => {
    try {
      const ctx = getAudioContext();
      
      // Bruit de carte + léger "swoosh"
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 0.1);
      osc.type = 'triangle';
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  },

  // Son de clic
  click: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 600;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.03);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.03);
    } catch (e) {}
  },

  // Son de mauvaise réponse
  wrong: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(300, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(150, ctx.currentTime + 0.2);
      osc.type = 'sawtooth';
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  },

  // Son de suspense
  suspense: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 200;
      osc.type = 'sine';
      
      lfo.frequency.value = 8;
      lfoGain.gain.value = 20;
      
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 1);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.5);
      
      lfo.start(ctx.currentTime);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 1.5);
      lfo.stop(ctx.currentTime + 1.5);
    } catch (e) {}
  },

  // Son de corde d'arc qui se tend
  bowDraw: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      // Son grave qui monte en tension
      osc.frequency.setValueAtTime(80, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.8);
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.8);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.8);
    } catch (e) {}
  },

  // Son de flèche qui part (corde relâchée + whoosh)
  bowRelease: () => {
    try {
      const ctx = getAudioContext();
      
      // Son de corde qui claque
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.frequency.setValueAtTime(400, ctx.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.15);
      osc1.type = 'triangle';
      gain1.gain.setValueAtTime(0.3, ctx.currentTime);
      gain1.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.15);
      
      // Whoosh de la flèche
      setTimeout(() => {
        const noise = ctx.createOscillator();
        const noiseGain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        
        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(ctx.destination);
        
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(2000, ctx.currentTime);
        filter.frequency.linearRampToValueAtTime(500, ctx.currentTime + 0.3);
        
        noise.type = 'sawtooth';
        noise.frequency.setValueAtTime(100, ctx.currentTime);
        
        noiseGain.gain.setValueAtTime(0.1, ctx.currentTime);
        noiseGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        
        noise.start(ctx.currentTime);
        noise.stop(ctx.currentTime + 0.3);
      }, 50);
    } catch (e) {}
  },

  // Son d'impact de flèche
  arrowImpact: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.1);
      osc.type = 'square';
      
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  },

  // Son de coffre qui s'ouvre
  chestOpen: () => {
    try {
      const ctx = getAudioContext();
      
      // Grincement de charnière
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.3);
      osc.type = 'sawtooth';
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.15);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  },

  // 🎰 Son de levier de machine à sous qui se tire
  slotLever: () => {
    try {
      const ctx = getAudioContext();
      
      // Cliquetis métallique du levier
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      
      filter.type = 'highpass';
      filter.frequency.value = 1000;
      
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.15);
      osc.type = 'square';
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
      
      // Son de ressort
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.setValueAtTime(300, ctx.currentTime);
        osc2.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1);
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.1, ctx.currentTime);
        gain2.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 0.1);
      }, 100);
    } catch (e) {}
  },

  // 🎡 Son de roue qui accélère
  wheelStart: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      // Accélération progressive
      osc.frequency.setValueAtTime(50, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.5);
      osc.type = 'triangle';
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.2);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  },

  // 💎 Son de découverte de diamant
  gemReveal: () => {
    try {
      const ctx = getAudioContext();
      
      // Son cristallin brillant
      const frequencies = [1200, 1500, 1800, 2400];
      frequencies.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(0.08, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        }, i * 50);
      });
    } catch (e) {}
  },

  // 🃏 Son de mélange de cartes
  cardShuffle: () => {
    try {
      const ctx = getAudioContext();
      
      for (let i = 0; i < 6; i++) {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(ctx.destination);
          
          filter.type = 'bandpass';
          filter.frequency.value = 2000 + Math.random() * 1000;
          filter.Q.value = 2;
          
          osc.frequency.value = 200 + Math.random() * 100;
          osc.type = 'sawtooth';
          
          gain.gain.setValueAtTime(0.06, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.05);
        }, i * 40 + Math.random() * 20);
      }
    } catch (e) {}
  },

  // 🎲 Son de dé qui rebondit
  diceBounce: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.1);
      osc.type = 'square';
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  },

  // ✨ Son magique de révélation
  magicReveal: () => {
    try {
      const ctx = getAudioContext();
      
      // Arpège magique ascendant
      const notes = [392, 494, 587, 784, 988]; // Sol Si Ré Sol Si (ascendant)
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(0.12, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.4);
        }, i * 80);
      });
    } catch (e) {}
  },

  // 🌟 Son d'étoile qui scintille
  starTwinkle: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 1500;
      osc.type = 'sine';
      lfo.frequency.value = 15;
      lfoGain.gain.value = 100;
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      
      lfo.start(ctx.currentTime);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
      lfo.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  },

  // 🎺 Fanfare de victoire majeure (pour gros gains)
  bigWinFanfare: () => {
    try {
      const ctx = getAudioContext();
      
      // Fanfare triomphante en Do Majeur
      const melody = [
        { freq: 523, time: 0, dur: 0.15 },      // Do
        { freq: 523, time: 150, dur: 0.1 },     // Do
        { freq: 523, time: 250, dur: 0.1 },     // Do
        { freq: 659, time: 350, dur: 0.3 },     // Mi
        { freq: 587, time: 650, dur: 0.1 },     // Ré
        { freq: 659, time: 750, dur: 0.1 },     // Mi
        { freq: 784, time: 850, dur: 0.4 },     // Sol
        { freq: 659, time: 1250, dur: 0.15 },   // Mi
        { freq: 784, time: 1400, dur: 0.15 },   // Sol
        { freq: 1047, time: 1550, dur: 0.5 },   // Do aigu
      ];
      
      melody.forEach(({ freq, time, dur }) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'square';
          
          gain.gain.setValueAtTime(0.12, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + dur);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + dur);
        }, time);
      });
      
      // Tambour de fond
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 80;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      }, 0);
      
      // Pièces qui tombent
      setTimeout(() => GameSounds.coins(), 800);
      setTimeout(() => GameSounds.coins(), 1200);
    } catch (e) {}
  },

  // 🔄 Son de réessayer / Replay
  replay: () => {
    try {
      const ctx = getAudioContext();
      
      // Mélodie joyeuse "lucky"
      const notes = [523, 659, 784, 659, 523]; // Do Mi Sol Mi Do
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'triangle';
          
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.15);
        }, i * 100);
      });
    } catch (e) {}
  },

  // 🎁 Son de cadeau / réduction
  giftSound: () => {
    try {
      const ctx = getAudioContext();
      
      // Mélodie "surprise"
      const notes = [
        { freq: 392, time: 0 },     // Sol
        { freq: 494, time: 100 },   // Si
        { freq: 587, time: 200 },   // Ré
        { freq: 784, time: 300 },   // Sol aigu
      ];
      
      notes.forEach(({ freq, time }) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'sine';
          
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.2);
        }, time);
      });
      
      // Petit "pop" de surprise
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.05);
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.05);
      }, 400);
    } catch (e) {}
  },

  // 🐍 Son de mouvement Snake
  snakeMove: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 200;
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.03);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.03);
    } catch (e) {}
  },

  // 🍎 Son de manger une pomme (Snake)
  snakeEat: () => {
    try {
      const ctx = getAudioContext();
      
      // Pop satisfaisant
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
      osc.type = 'sine';
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {}
  },

  // 💀 Son de game over (Snake)
  snakeDeath: () => {
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.5);
      osc.type = 'sawtooth';
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
      
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  },

  // 🎯 Son de bullseye (centre de la cible)
  bullseye: () => {
    try {
      const ctx = getAudioContext();
      
      // Arpège triomphant
      const notes = [523, 659, 784, 1047]; // Do Mi Sol Do
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.value = freq;
          osc.type = 'triangle';
          
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.2);
        }, i * 80);
      });
      
      // Ajout de scintillement
      setTimeout(() => GameSounds.starTwinkle(), 300);
    } catch (e) {}
  },
};

export default GameSounds;
